import { Box, Button, Grid, Typography, CircularProgress } from '@mui/material'
import React from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { getCmsContent, getPlansByOption } from '../Apis/CategoriesApis'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import check from '../assets/check-circle.svg'

const ServiceDetails = () => {
  const { id } = useParams()
  const location = useLocation()
  const [service, setService] = React.useState(location.state ? {
    ...location.state,
    // Normalize distinct keys from list vs detail API if necessary
    priceStartText: 'Contact for pricing',
    note: location.state.BoxText,
    CategoryOptions: location.state.options || location.state.CategoryOptions || [],
    plans: location.state.plans || []
  } : null)

  const [selectedOption, setSelectedOption] = React.useState(
    location.state?.CategoryOptions?.[0]?.id || location.state?.options?.[0]?.id || null
  )
  const [selectedPlan, setSelectedPlan] = React.useState(
    location.state?.plans?.[0] || null
  )
  const [cmsContentsList, setCmsContentsList] = React.useState([])
  const [apiLoading, setApiLoading] = React.useState(false)
  const [initialLoading, setInitialLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true;
    let apiFinished = false;
    let timerFinished = false;

    const checkAndSetInitialLoading = () => {
      if (apiFinished && timerFinished && isMounted) {
        setInitialLoading(false);
      }
    };

    const minTimer = setTimeout(() => {
      timerFinished = true;
      checkAndSetInitialLoading();
    }, 800);

    if (id) {
      setApiLoading(true);
      getCmsContent(id, selectedOption).then((data) => {
        if (!isMounted) return;
        if (data && data.status) {
          const allCmsItems = Array.isArray(data.data) ? data.data : [];
          setCmsContentsList(allCmsItems);

          // Extract unique plans from API if not already present in service state
          const apiPlans = [...new Set(allCmsItems.map(item => item.plan).filter(Boolean))];

          // If no plan is selected, try to pick the first one from the results
          let cmsData = null;
          if (selectedPlan) {
            cmsData = allCmsItems.find(item => item.plan === selectedPlan);
          }

          if (!cmsData && allCmsItems.length > 0) {
            cmsData = allCmsItems[0];
            // Auto-select the first plan to keep state in sync
            if (!selectedPlan && isMounted) {
              setSelectedPlan(cmsData.plan);
            }
          }

          let description = cmsData?.description || '';

          setService(prev => {
            const baseName = prev?.name || 'Service Details';
            const updatedPlans = apiPlans;

            if (!cmsData) {
              return {
                ...prev,
                CategoryOptions: prev?.CategoryOptions || [],
                plans: updatedPlans,
                heroTitle: baseName,
                priceStartText: 'Contact for pricing',
                workFlow: [],
                note: location.state?.BoxText || '',
                Category: prev?.Category || {}
              }
            }

            return {
              ...prev,
              ...cmsData,
              name: cmsData.Category?.name || baseName,
              priceStartText: cmsData.PriceText || 'Contact for pricing',
              heroTitle: cmsData.title || baseName,
              description: description,
              note: location.state?.BoxText || cmsData.BoxText || '',
              CategoryOptions: prev?.CategoryOptions || [],
              plans: updatedPlans
            }
          });

          // Sync selectedPlan if the current one is not in the new plans list
          if (apiPlans.length > 0 && (!selectedPlan || !apiPlans.includes(selectedPlan))) {
            setSelectedPlan(apiPlans[0]);
          }
        }
      }).finally(() => {
        if (isMounted) {
          setApiLoading(false);
          apiFinished = true;
          checkAndSetInitialLoading();
        }
      });
    } else {
      // If no ID, still wait for the minimum timer before showing content
      apiFinished = true;
      checkAndSetInitialLoading();
    }

    return () => {
      isMounted = false;
      clearTimeout(minTimer);
    }
  }, [id, selectedOption, selectedPlan])


  const openConsultationForm = (serviceName) => {
    window.dispatchEvent(
      new CustomEvent('open-consultation-form', {
        detail: { source: 'service-details', service: serviceName }
      })
    )
  }

  /* Removed duplicate state declarations */

  /* Removed auto-selection logic to default to no visual selection while showing API content */

  if (initialLoading) {
    return (
      <Box sx={{ py: { xs: 3, md: 6 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress sx={{ color: '#ff6a00' }} />
      </Box>
    )
  }

  return (
    <Box sx={{
      py: { xs: 3, md: 6 },
      opacity: initialLoading ? 0 : 1,
      transition: 'opacity 0.5s ease-in-out'
    }}>
      <Grid container spacing={{ xs: 3, md: 4 }}>
        {/* Left: Image */}
        <Grid item size={{ xs: 12, md: 4 }}>
          <Box
            sx={{
              width: '100%',
              height: { xs: 280, md: 430 },
              borderRadius: '12px',
              overflow: 'hidden',
              position: 'relative',
              backgroundColor: '#f5f5f5',
              backgroundImage: `url(${service.Category?.image || service.image || service.heroImage || service.logo || ''})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(180deg, rgba(0,0,0,0.15) 45%, rgba(0,0,0,0.75) 100%)'
              }}
            />
            <Typography
              sx={{
                position: 'absolute',
                left: 16,
                bottom: 18,
                color: '#fff',
                fontWeight: 800,
                fontSize: { xs: 22, md: 26 },
                maxWidth: '90%'
              }}
            >
              {service.heroTitle || service.name || location.state?.name || 'Service Details'}
            </Typography>
          </Box>
        </Grid>

        {/* Middle: Details & Workflow */}
        <Grid item size={{ xs: 12, md: 5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              sx={{
                fontSize: { xs: 20, md: 22 },
                fontWeight: 700,
                color: '#000'
              }}
            >
              {service.name}
            </Typography>




            {(service.CategoryOptions || service.options)?.length ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {(service.CategoryOptions || service.options).map((opt) => (
                  <Box
                    key={opt.id}
                    onClick={() => setSelectedOption(opt.id)}
                    sx={{
                      px: 1.5,
                      py: 0.6,
                      borderRadius: '6px',
                      border: selectedOption === opt.id ? '1px solid #ff6a00' : '1px solid #E5E5E5',
                      backgroundColor: '#fff',
                      color: selectedOption === opt.id ? '#ff6a00' : '#000',
                      cursor: 'pointer'
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {opt.optionName}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : null}

            {service.plans?.length ? (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {service.plans.map((plan) => {
                  // Find price text for this plan
                  const planInfo = cmsContentsList.find(i => i.plan === plan);
                  const priceText = planInfo?.PriceText || '';

                  return (
                    <Box
                      key={plan}
                      onClick={() => setSelectedPlan(plan)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer'
                      }}
                    >
                      {selectedPlan === plan ? (
                        <RadioButtonCheckedIcon sx={{ fontSize: 18, color: '#ff6a00' }} />
                      ) : (
                        <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: '#CFCFCF' }} />
                      )}
                      <Box>
                        <Typography sx={{ fontSize: 14, color: '#000', fontWeight: 500 }}>
                          {plan}
                        </Typography>

                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : null}

            {/* Price Tags moved to bottom of plans */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {(() => {
                let tags = [];
                try {
                  if (service.priceStartText && typeof service.priceStartText === 'string' && service.priceStartText.startsWith('[')) {
                    tags = JSON.parse(service.priceStartText);
                  } else if (Array.isArray(service.priceStartText)) {
                    tags = service.priceStartText;
                  } else {
                    tags = [service.priceStartText || 'Best Price Guaranteed'];
                  }
                } catch (e) {
                  tags = [service.priceStartText || 'Best Price Guaranteed'];
                }
                return tags.map((tag, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: '#FFE9DD',
                      px: 2,
                      py: 0.8,
                      borderRadius: '6px'
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#ff6a00',
                        fontSize: 14,
                        fontWeight: 600,
                        fontStyle: 'italic'
                      }}
                    >
                      {tag}
                    </Typography>
                  </Box>
                ));
              })()}
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  fontStyle: 'italic',
                  color: '#000',
                  mb: 1.5
                }}
              >
                {service.workFlowTitle ?? 'Varahi work flow'}
              </Typography>

              <Box
                sx={{
                  fontSize: 13,
                  color: '#444',
                  lineHeight: 1.6,
                  maxWidth: { md: '640px' },
                  width: '100%',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  '& p': { mb: 1.5 },
                  '& ul': {
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    '& li': {
                      position: 'relative',
                      paddingLeft: '24px',
                      marginBottom: '10px',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '2px',
                        width: '14px',
                        height: '14px',
                        backgroundImage: `url(${check})`,
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat'
                      }
                    }
                  },
                  '& ol': {
                    paddingLeft: '20px',
                    '& li': {
                      marginBottom: '8px'
                    }
                  }
                }}
                dangerouslySetInnerHTML={{ __html: service.description }}
              />
            </Box>
          </Box>
        </Grid>

        {/* Right: Actions */}
        <Grid item size={{ xs: 12, md: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' }, gap: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => openConsultationForm(service.name)}
              sx={{
                backgroundColor: '#ff6a00',
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                py: 1.5,
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#e85f00', boxShadow: 'none' }
              }}
            >
              Book free consultation
            </Button>
            {service.note && (
              <Box
                sx={{
                  backgroundColor: '#F3F6B6',
                  borderRadius: '8px',
                  p: 2,
                  textAlign: 'center',
                  width: '100%',
                  opacity: apiLoading ? 0.7 : 1,
                  transition: 'opacity 0.2s'
                }}
              >
                <Typography sx={{ fontSize: 15, color: '#222', fontWeight: 600, lineHeight: 1.5 }}>
                  {service.note}
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default ServiceDetails
